/**
 * ContextualReadinessPanel
 *
 * Shows the resolution result for any node the user has arrived at.
 * Surfaces only actionable conditions — met and inherited are silent.
 *
 * Used at: cycle overview, track overview, session entry, tool selection,
 * and Catchball track readiness panels.
 *
 * Canon §08 §6: No disabled CTAs. Blocked states show named blocker cards
 * with remediation links. Never vague diagnostics.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Clock, ArrowRight, CheckCircle2, User } from 'lucide-react';
import {
  resolveFromCycle,
  resolveFromTrack,
  resolveFromSession,
  resolveFromTool,
} from '../../lib/conditionResolver';
import type { ResolutionResult, ResolvedCondition } from '../../lib/conditionResolver';
import { useUserStore } from '../../stores/prioritization/userStore';

interface ContextualReadinessPanelProps {
  nodeType: 'cycle' | 'track' | 'session' | 'tool';
  nodeId: string;
  /** Required when nodeType is 'tool' */
  sessionId?: string;
  /** If true, panel renders nothing when all conditions are met */
  collapseWhenReady?: boolean;
  /** If true, shows the role-filtered view (only conditions this user can act on) */
  roleFiltered?: boolean;
  /** Workspace ID — falls back to demo workspace when absent */
  workspaceId?: string;
  /**
   * Dense single-panel layout: one bordered shell with thin rows instead of
   * stacked full cards. Prefer on Session detail so tabs/header stay visible.
   */
  compact?: boolean;
}

function conditionIcon(state: ResolvedCondition['state'], size: 'sm' | 'md' = 'md') {
  const cls = size === 'sm' ? 'h-3.5 w-3.5 shrink-0 mt-px' : 'h-4 w-4 shrink-0 mt-0.5';
  switch (state) {
    case 'missing':
      return <AlertTriangle className={`${cls} text-[var(--mustard)] dark:text-[var(--mustard)]`} />;
    case 'blocked':
      return <Clock className={`${cls} text-[var(--text-ter)]`} />;
    default:
      return <AlertTriangle className={`${cls} text-[var(--coral)] dark:text-[var(--coral)]`} />;
  }
}

function conditionCardClass(state: ResolvedCondition['state']) {
  switch (state) {
    case 'missing':
      return 'bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/25 border border-[var(--mustard)] dark:border-[var(--mustard)]/50';
    case 'blocked':
      return 'bg-[var(--surface)] dark:bg-[var(--bg)]/35 border border-[var(--rule)] text-[var(--text-sec)]';
    case 'conflicting':
      return 'bg-[var(--coral-dim)] dark:bg-[var(--coral)]/25 border border-[var(--coral)] dark:border-[var(--coral)]/50';
    default:
      return 'bg-[var(--surface)] dark:bg-[var(--bg)]/35 border border-[var(--rule)]';
  }
}

function ConditionCard({ condition }: { condition: ResolvedCondition }) {
  const isBlocked = condition.state === 'blocked';
  const hasLink = Boolean(condition.remediationPath);

  const inner = (
    <div className="flex items-start gap-3">
      {conditionIcon(condition.state)}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-[var(--text-pri)]">{condition.label}</p>
            {isBlocked && condition.blockedBy && (
              <p className="mt-0.5 text-xs text-[var(--text-ter)]">
                Waiting on: <span className="font-medium">{condition.blockedBy}</span>
              </p>
            )}
            {condition.detail && (
              <p className="mt-0.5 text-xs text-[var(--text-sec)]">{condition.detail}</p>
            )}
          </div>
          {hasLink && (
            <span className="inline-flex items-center gap-1 shrink-0 text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] group-hover:text-[var(--text-ter)] dark:group-hover:text-[var(--text-ter)]">
              Fix
              <ArrowRight className="h-3 w-3" />
            </span>
          )}
        </div>
      </div>
    </div>
  );

  if (hasLink && condition.remediationPath) {
    return (
      <Link
        to={condition.remediationPath}
        className={`group block  px-4 py-3 hover:opacity-90 transition-opacity cursor-pointer ${conditionCardClass(condition.state)}`}
      >
        {inner}
      </Link>
    );
  }

  return (
    <div className={` px-4 py-3 ${conditionCardClass(condition.state)}`}>
      {inner}
    </div>
  );
}

/** Single-row variant for compact shell — preserves remediation links */
function CompactConditionRow({ condition }: { condition: ResolvedCondition }) {
  const isBlocked = condition.state === 'blocked';
  const hasLink = Boolean(condition.remediationPath);

  const rowInner = (
    <div className="flex items-start gap-2 min-w-0 flex-1">
      {conditionIcon(condition.state, 'sm')}
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-[var(--text-pri)] leading-snug">{condition.label}</p>
        {(isBlocked && condition.blockedBy) || condition.detail ? (
          <p className="mt-0.5 text-[11px] text-[var(--text-sec)] leading-snug line-clamp-2">
            {isBlocked && condition.blockedBy ? (
              <>
                Waiting on <span className="font-medium">{condition.blockedBy}</span>
                {condition.detail ? ` · ${condition.detail}` : ''}
              </>
            ) : (
              condition.detail
            )}
          </p>
        ) : null}
      </div>
      {hasLink && (
        <span className="inline-flex items-center gap-0.5 shrink-0 text-[11px] font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] group-hover:text-[var(--text-ter)] dark:group-hover:text-[var(--text-ter)] pt-px">
          Fix
          <ArrowRight className="h-3 w-3" />
        </span>
      )}
    </div>
  );

  const rowClass =
    'flex items-start gap-2 px-3 py-2 transition-colors ' +
    (condition.state === 'missing'
      ? 'hover:bg-[var(--mustard-dim)]/40 dark:hover:bg-[var(--mustard)]/45'
      : condition.state === 'blocked'
        ? 'opacity-75 hover:bg-[var(--surface)] dark:hover:bg-[var(--bg)]/50'
        : condition.state === 'conflicting'
          ? 'hover:bg-[var(--coral-dim)]/80 dark:hover:bg-[var(--coral)]/30'
          : 'hover:bg-[var(--surface)] dark:hover:bg-[var(--bg)]/50');

  if (hasLink && condition.remediationPath) {
    return (
      <li className="list-none">
        <Link to={condition.remediationPath} className={`group block ${rowClass}`}>
          {rowInner}
        </Link>
      </li>
    );
  }

  return (
    <li className={`list-none ${rowClass}`}>
      <div className="flex w-full">{rowInner}</div>
    </li>
  );
}


export function ContextualReadinessPanel({
  nodeType,
  nodeId,
  sessionId,
  collapseWhenReady = false,
  roleFiltered = false,
  workspaceId = 'demo',
  compact = false,
}: ContextualReadinessPanelProps) {
  const userId = useUserStore((s) => s.userId);
  const [result, setResult] = useState<ResolutionResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId || !nodeId) return;

    setLoading(true);
    let cancelled = false;

    const fetch = async () => {
      try {
        let r: ResolutionResult;
        if (nodeType === 'cycle') {
          r = await resolveFromCycle(nodeId, userId, workspaceId);
        } else if (nodeType === 'track') {
          r = await resolveFromTrack(nodeId, userId, workspaceId);
        } else if (nodeType === 'session') {
          r = await resolveFromSession(nodeId, userId, workspaceId);
        } else {
          if (!sessionId) return;
          r = await resolveFromTool(nodeId, sessionId, userId, workspaceId);
        }
        if (!cancelled) setResult(r);
      } catch {
        // silently fail — this is a readiness helper, not a gate
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void fetch();
    return () => { cancelled = true; };
  }, [nodeType, nodeId, sessionId, userId, workspaceId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-3 text-sm text-[var(--text-ter)]">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-[var(--rule)] border-t-gray-500 dark:border-t-neutral-300" />
        Checking readiness…
      </div>
    );
  }

  if (!result) return null;

  // Determine which conditions to surface
  const conditionsToShow = roleFiltered && result.roleView
    ? result.roleView.relevantConditions
    : result.actionableConditions;

  // Waiting-on state: user's role has nothing to act on but things are still blocked
  const waitingOn =
    roleFiltered && result.roleView && !result.roleView.canAct && result.actionableConditions.length > 0
      ? result.roleView.waitingOn
      : null;

  // Ready and collapse requested
  if (result.ready && collapseWhenReady) return null;

  // Ready, no collapse — show a small confirmation
  if (result.ready) {
    return (
      <div className="flex items-center gap-2 py-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
        <CheckCircle2 className="h-4 w-4 shrink-0" />
        All conditions met
      </div>
    );
  }

  // Waiting on someone else
  if (waitingOn && conditionsToShow.length === 0) {
    return (
      <div className="flex items-center gap-2 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 border border-[var(--slate)] dark:border-[var(--slate)]/50 px-4 py-3 text-sm text-[var(--text-sec)]">
        <User className="h-4 w-4 shrink-0" />
        Waiting on {waitingOn} to complete required setup.
      </div>
    );
  }

  // No conditions to show after filtering but node not ready
  if (conditionsToShow.length === 0) {
    return null;
  }

  if (compact) {
    return (
      <div
        className="border border-[var(--mustard)]/90 dark:border-[var(--mustard)]/45 bg-[var(--mustard-dim)]/85 dark:bg-[var(--mustard)]/25 overflow-hidden shadow-sm"
        role="region"
        aria-label="Outstanding setup steps"
      >
        <div className="flex items-center gap-2 px-3 py-2 border-b border-[var(--mustard)] dark:border-[var(--mustard)]/40 bg-[var(--mustard-dim)]/40 dark:bg-[var(--mustard)]/35">
          <AlertTriangle className="h-3.5 w-3.5 text-[var(--mustard)] dark:text-[var(--mustard)] shrink-0" />
          <span className="text-xs font-semibold text-[var(--mustard)] dark:text-[var(--mustard)]">Setup needed</span>
          <span className="text-[11px] font-medium tabular-nums text-[var(--mustard)] dark:text-[var(--mustard)] bg-[var(--mustard-dim)]/60 dark:bg-[var(--mustard)]/50 px-1.5 py-0.5 rounded">
            {conditionsToShow.length}
          </span>
        </div>
        <ul className="list-none m-0 p-0 divide-y divide-amber-100/80 dark:divide-amber-900/35">
          {conditionsToShow.map((condition) => (
            <CompactConditionRow key={condition.code} condition={condition} />
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {conditionsToShow.map((condition) => (
        <ConditionCard key={condition.code} condition={condition} />
      ))}
    </div>
  );
}

export default ContextualReadinessPanel;
